import { Button } from "@/components/ui/button"
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Link, useNavigate } from "react-router-dom"
import { useGetUserById, useUpdateUserName } from "@/api/userQueries"
import { useUpdateUserPassword } from "@/api/authQueries"
import {
    recoveryCall,
    recoveryEnabled,
    reauthenticate,
    type RecoveryContact,
} from "@/api/recovery"
import { Label } from "@/components/ui/label"

import { useState, useEffect, useRef, ChangeEvent } from "react"
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query"
import { Eye, EyeOff, Info, Loader2 } from "lucide-react"
import { Separator } from "@/components/ui/separator"

import toast from "react-hot-toast"
import { APP_ROUTES } from "@/common/urls"

export function Account() {
    const [showPasswordFields, setShowPasswordFields] = useState(false)
    const [currentPw, setCurrentPw] = useState("")
    const [newPw, setNewPw] = useState("")
    const [confirmPw, setConfirmPw] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [firstName, setFirstName] = useState("")
    const [lastName, setLastName] = useState("")
    const [contactPassword, setContactPassword] = useState("")
    const [showRecoveryFields, setShowRecoveryFields] = useState(false)
    const [secondaryEmail, setSecondaryEmail] = useState("")
    const [secondaryFirstName, setSecondaryFirstName] = useState("")
    const [secondaryLastName, setSecondaryLastName] = useState("")
    const [recoveryErrors, setRecoveryErrors] = useState({
        email: false,
        firstName: false,
        lastName: false,
    })
    const [pwMatchError, setPwMatchError] = useState(false)
    const [isAccountTransferPending, setIsAccountTransferPending] =
        useState(false)
    const timerRef = useRef<number | null>(null)
    const navFunc = useNavigate()
    const queryClient = useQueryClient()
    const userDetails = useGetUserById(navFunc)
    const updatePasswordMutation = useUpdateUserPassword({
        onSuccess: () => {
            toast.success("Password updated. Please sign in again.")
            setShowPasswordFields(false)
            resetStates()
            navFunc(APP_ROUTES.AUTH.LOGIN)
        },
        onError: (error: unknown) => {
            if (error instanceof Error && "code" in error) {
                if (
                    error.code === "auth/wrong-password" ||
                    error.message.includes("wrong-password")
                ) {
                    toast.error("Current password incorrect")
                } else {
                    toast.error("Password update failed")
                }
            } else {
                toast.error("Password update failed")
            }
        },
    })

    useEffect(() => {
        if (userDetails.data) {
            setFirstName(userDetails.data.firstName)
            setLastName(userDetails.data.lastName)
            setSecondaryEmail(userDetails.data.secondaryEmail || "")
            setSecondaryFirstName(userDetails.data.secondaryFirstName || "")
            setSecondaryLastName(userDetails.data.secondaryLastName || "")
            setIsAccountTransferPending(
                userDetails.data.isAccountOwnershipTransferPending ?? false
            )
        }
    }, [userDetails.data])

    const hasChanges =
        userDetails.data &&
        (userDetails.data.firstName !== firstName ||
            userDetails.data.lastName !== lastName)

    const hasSecondary =
        userDetails.data?.secondaryEmail &&
        userDetails.data?.secondaryFirstName &&
        userDetails.data?.secondaryLastName

    const updateNameMutation = useUpdateUserName()

    const contactStatus = useQuery({
        queryKey: ["recoveryContact"],
        queryFn: () => recoveryCall<RecoveryContact>("getRecoveryContact"),
        enabled: recoveryEnabled && userDetails.data?.role === "applicant",
    })
    const updateSecondaryMutation = useMutation({
        mutationFn: async ({
            secondaryEmail,
            secondaryFirstName,
            secondaryLastName,
        }: {
            secondaryEmail: string
            secondaryFirstName: string
            secondaryLastName: string
        }) => {
            await reauthenticate(contactPassword)
            await recoveryCall("enrolRecoveryContact", {
                email: secondaryEmail,
                firstName: secondaryFirstName,
                lastName: secondaryLastName,
            })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["userDetails"] })
            toast.success(
                "Verification requested. The contact must confirm their inbox."
            )
            queryClient.invalidateQueries({ queryKey: ["recoveryContact"] })
            setContactPassword("")
            setShowRecoveryFields(false)
            setSecondaryEmail("")
            setSecondaryFirstName("")
            setSecondaryLastName("")
            setRecoveryErrors({
                email: false,
                firstName: false,
                lastName: false,
            })
        },
        onError: () => {
            toast.error("Failed to add recovery contact")
        },
    })

    const handleSaveName = async () => {
        if (!hasChanges || updateNameMutation.isPending) return
        try {
            await updateNameMutation.mutateAsync({ firstName, lastName })
            queryClient.invalidateQueries({ queryKey: ["userDetails"] })
            toast.success("Name updated")
        } catch {
            toast.error("Update failed")
        }
    }

    const handleCancelName = () => {
        if (userDetails.data) {
            setFirstName(userDetails.data.firstName)
            setLastName(userDetails.data.lastName)
        }
    }

    const handleSaveRecovery = () => {
        const errors = {
            email:
                !secondaryEmail.trim() ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(secondaryEmail),
            firstName: !secondaryFirstName.trim(),
            lastName: !secondaryLastName.trim(),
        }
        setRecoveryErrors(errors)
        if (
            errors.email ||
            errors.firstName ||
            errors.lastName ||
            !contactPassword
        )
            return
        updateSecondaryMutation.mutate({
            secondaryEmail: secondaryEmail.trim(),
            secondaryFirstName: secondaryFirstName.trim(),
            secondaryLastName: secondaryLastName.trim(),
        })
    }

    const handleToggleFields = () => {
        const newShow = !showPasswordFields
        setShowPasswordFields(newShow)
        if (!newShow) resetStates()
    }
    const handleCurrentPwChange = (e: ChangeEvent<HTMLInputElement>) =>
        setCurrentPw(e.target.value)
    const handleNewPwChange = (e: ChangeEvent<HTMLInputElement>) =>
        setNewPw(e.target.value)
    const handleConfirmPwChange = (e: ChangeEvent<HTMLInputElement>) =>
        setConfirmPw(e.target.value)

    const resetStates = () => {
        setCurrentPw("")
        setNewPw("")
        setConfirmPw("")
        setPwMatchError(false)
    }

    const handleSave = () => {
        if (
            pwMatchError ||
            !currentPw.trim() ||
            !newPw.trim() ||
            !confirmPw.trim()
        ) {
            toast.error(
                "Please fill current password, new password, confirm password, and ensure they match"
            )
            return
        }
        updatePasswordMutation.mutate({
            currentPassword: currentPw.trim(),
            newPassword: newPw.trim(),
        })
    }

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current)
        const t = window.setTimeout(() => {
            setPwMatchError(
                newPw.length > 0 && confirmPw.length > 0 && newPw !== confirmPw
            )
        }, 500)
        timerRef.current = t
        return () => clearTimeout(t)
    }, [confirmPw, newPw])

    return (
        <div className="flex min-h-screen w-full flex-col pb-20 pt-8 bg-muted/40">
            <main className="flex min-h-[calc(100vh_-_theme(spacing.16))] flex-1 flex-col gap-4 p-4 md:gap-8 md:p-10">
                <div className="mx-auto grid w-full max-w-2xl items-start gap-6">
                    <div className="grid gap-6">
                        <Card x-chunk="dashboard-04-chunk-1">
                            <CardHeader className="px-16 py-10">
                                <div className="flex items-center justify-between">
                                    <CardTitle>Account</CardTitle>
                                    {hasChanges && (
                                        <div className="flex gap-2">
                                            <Button
                                                onClick={handleSaveName}
                                                disabled={
                                                    !hasChanges ||
                                                    updateNameMutation.isPending
                                                }
                                                className="min-w-[100px]"
                                            >
                                                {updateNameMutation.isPending ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    "Save changes"
                                                )}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                onClick={handleCancelName}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="flex flex-col gap-6 px-16">
                                <form
                                    className="flex flex-col gap-6"
                                    onSubmit={(e) => e.preventDefault()}
                                >
                                    <div>
                                        <Label htmlFor="firstName">
                                            First Name
                                        </Label>
                                        <Input
                                            id="firstName"
                                            value={firstName}
                                            onChange={(e) =>
                                                setFirstName(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="lastName">
                                            Last Name
                                        </Label>
                                        <Input
                                            id="lastName"
                                            value={lastName}
                                            onChange={(e) =>
                                                setLastName(e.target.value)
                                            }
                                        />
                                    </div>
                                    <div className="flex justify-between">
                                        <div className="flex flex-col gap-2">
                                            <Label htmlFor="lastName">
                                                Password
                                            </Label>
                                            <span className="opacity-40">
                                                ••••••••
                                            </span>
                                        </div>
                                        {!showPasswordFields ? (
                                            <Button
                                                variant="outline"
                                                onClick={handleToggleFields}
                                                className="flex justify-center items-center gap-2 text-app-defaultLight border-app-defaultLight hover:text-app-defaultLight"
                                            >
                                                Change Password
                                            </Button>
                                        ) : (
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="default"
                                                    onClick={handleSave}
                                                    disabled={
                                                        pwMatchError ||
                                                        updatePasswordMutation.isPending ||
                                                        !currentPw.trim() ||
                                                        !newPw.trim() ||
                                                        !confirmPw.trim()
                                                    }
                                                    className="min-w-[120px]"
                                                >
                                                    {updatePasswordMutation.isPending ? (
                                                        <>
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        "Save Changes"
                                                    )}
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    onClick={handleToggleFields}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </form>
                                {showPasswordFields && (
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <Label>Current Password</Label>
                                            <div className="relative">
                                                <Input
                                                    type={
                                                        showPassword
                                                            ? "text"
                                                            : "password"
                                                    }
                                                    placeholder="Enter current password"
                                                    value={currentPw}
                                                    onChange={
                                                        handleCurrentPwChange
                                                    }
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-3 top-1/2 h-5 w-5 p-0 -translate-y-1/2 opacity-70 hover:opacity-100"
                                                    onClick={() =>
                                                        setShowPassword(
                                                            (p) => !p
                                                        )
                                                    }
                                                >
                                                    {showPassword ? (
                                                        <EyeOff className="h-4 w-4" />
                                                    ) : (
                                                        <Eye className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                        <div>
                                            <Label>New Password</Label>
                                            <div className="relative">
                                                <Input
                                                    type={
                                                        showPassword
                                                            ? "text"
                                                            : "password"
                                                    }
                                                    placeholder="Enter new password"
                                                    className={
                                                        pwMatchError
                                                            ? "border-destructive focus-visible:ring-destructive"
                                                            : ""
                                                    }
                                                    value={newPw}
                                                    onChange={handleNewPwChange}
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-3 top-1/2 h-5 w-5 p-0 -translate-y-1/2 opacity-70 hover:opacity-100"
                                                    onClick={() =>
                                                        setShowPassword(
                                                            (p) => !p
                                                        )
                                                    }
                                                >
                                                    {showPassword ? (
                                                        <EyeOff className="h-4 w-4" />
                                                    ) : (
                                                        <Eye className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                        <div>
                                            <Label>Confirm New Password</Label>
                                            <div className="relative">
                                                <Input
                                                    type={
                                                        showPassword
                                                            ? "text"
                                                            : "password"
                                                    }
                                                    placeholder="Confirm new password"
                                                    className={
                                                        pwMatchError
                                                            ? "border-destructive focus-visible:ring-destructive"
                                                            : ""
                                                    }
                                                    value={confirmPw}
                                                    onChange={
                                                        handleConfirmPwChange
                                                    }
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="absolute right-3 top-1/2 h-5 w-5 p-0 -translate-y-1/2 opacity-70 hover:opacity-100"
                                                    onClick={() =>
                                                        setShowPassword(
                                                            (p) => !p
                                                        )
                                                    }
                                                >
                                                    {showPassword ? (
                                                        <EyeOff className="h-4 w-4" />
                                                    ) : (
                                                        <Eye className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                            {pwMatchError && (
                                                <p className="text-xs text-destructive mt-1">
                                                    Passwords do not match
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                                <Separator />
                                <div className="flex justify-between items-center">
                                    <div className="flex flex-col gap-2">
                                        <Label htmlFor="email">
                                            Email Address
                                        </Label>
                                        <p className="text-sm opacity-50">
                                            {userDetails.data
                                                ? `${userDetails.data.email}`
                                                : "Email address not found"}
                                        </p>
                                    </div>
                                    {isAccountTransferPending ? (
                                        <div className="text-orange-600 text-sm flex gap-2 items-center font-medium">
                                            <div className="size-2 bg-orange-600 rounded-full" />
                                            Account Transfer Pending
                                        </div>
                                    ) : userDetails.data?.role ===
                                      "applicant" ? (
                                        <Button
                                            asChild={true}
                                            variant={"link"}
                                            className="underline text-app-defaultLight"
                                        >
                                            <Link
                                                to={
                                                    APP_ROUTES.ROOT
                                                        .TRANSFER_OWNERSHIP
                                                }
                                            >
                                                Transfer Ownership
                                            </Link>
                                        </Button>
                                    ) : null}
                                </div>
                                <div className="flex flex-col items-start gap-2">
                                    <div className="flex gap-2 items-center">
                                        <Info className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                                        <p className="font-semibold text-xs leading-relaxed text-muted-foreground">
                                            Disclaimer:
                                        </p>
                                    </div>
                                    <p className="text-xs leading-relaxed text-muted-foreground">
                                        To update your email address, please
                                        reach out to our support team for
                                        assistance.
                                    </p>
                                </div>
                                <Separator />
                                {userDetails.data?.role === "applicant" && (
                                    <>
                                        <div className="flex flex-col gap-2">
                                            <Label htmlFor="secondaryEmailAddress">
                                                Secondary Email Address for
                                                Recovery
                                            </Label>
                                            <p className="text-sm opacity-50">
                                                {userDetails.data &&
                                                userDetails.data.secondaryEmail
                                                    ? `${userDetails.data.secondaryEmail}`
                                                    : "No secondary email address has been set"}
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <Label htmlFor="secondaryFirstName">
                                                Secondary First Name
                                            </Label>
                                            <p className="text-sm opacity-50">
                                                {userDetails.data &&
                                                userDetails.data
                                                    .secondaryFirstName
                                                    ? `${userDetails.data.secondaryFirstName}`
                                                    : "No secondary first name has been set"}
                                            </p>
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <Label htmlFor="secondaryLastName">
                                                Secondary Last Name
                                            </Label>
                                            <p className="text-sm opacity-50">
                                                {userDetails.data &&
                                                userDetails.data
                                                    .secondaryLastName
                                                    ? `${userDetails.data.secondaryLastName}`
                                                    : "No secondary last name has been set"}
                                            </p>
                                        </div>
                                    </>
                                )}

                                {recoveryEnabled &&
                                    userDetails.data?.role === "applicant" && (
                                        <>
                                            <p
                                                role="status"
                                                className="text-sm"
                                            >
                                                {contactStatus.isError
                                                    ? "Verification status is unavailable. Please try again later."
                                                    : contactStatus.data
                                                            ?.verified
                                                      ? "Recovery contact verified."
                                                      : "Verify your recovery contact so they can confirm a future recovery request. Until then, CMHT can manually verify your organisation. Your normal sign-in and password reset still work."}
                                            </p>
                                            {!showRecoveryFields && (
                                                <Button
                                                    variant="outline"
                                                    onClick={() =>
                                                        setShowRecoveryFields(
                                                            true
                                                        )
                                                    }
                                                >
                                                    {hasSecondary
                                                        ? "Verify or update recovery contact"
                                                        : "Add a recovery contact"}
                                                </Button>
                                            )}
                                            {showRecoveryFields && (
                                                <div className="flex flex-col gap-4">
                                                    <div>
                                                        <Label htmlFor="secondaryEmailInput">
                                                            Secondary Email
                                                        </Label>
                                                        <Input
                                                            id="secondaryEmailInput"
                                                            type="email"
                                                            value={
                                                                secondaryEmail
                                                            }
                                                            onChange={(e) =>
                                                                setSecondaryEmail(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className={
                                                                recoveryErrors.email
                                                                    ? "border-destructive"
                                                                    : ""
                                                            }
                                                        />
                                                        {recoveryErrors.email && (
                                                            <p className="text-xs text-destructive">
                                                                Invalid email
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <Label htmlFor="secondaryFirstNameInput">
                                                            Secondary First Name
                                                        </Label>
                                                        <Input
                                                            id="secondaryFirstNameInput"
                                                            value={
                                                                secondaryFirstName
                                                            }
                                                            onChange={(e) =>
                                                                setSecondaryFirstName(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className={
                                                                recoveryErrors.firstName
                                                                    ? "border-destructive"
                                                                    : ""
                                                            }
                                                        />
                                                        {recoveryErrors.firstName && (
                                                            <p className="text-xs text-destructive">
                                                                Required
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <Label htmlFor="secondaryLastNameInput">
                                                            Secondary Last Name
                                                        </Label>
                                                        <Input
                                                            id="secondaryLastNameInput"
                                                            value={
                                                                secondaryLastName
                                                            }
                                                            onChange={(e) =>
                                                                setSecondaryLastName(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            className={
                                                                recoveryErrors.lastName
                                                                    ? "border-destructive"
                                                                    : ""
                                                            }
                                                        />
                                                        {recoveryErrors.lastName && (
                                                            <p className="text-xs text-destructive">
                                                                Required
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label htmlFor="contactPassword">
                                                            Your current
                                                            password
                                                        </Label>
                                                        <Input
                                                            id="contactPassword"
                                                            type="password"
                                                            autoComplete="current-password"
                                                            value={
                                                                contactPassword
                                                            }
                                                            onChange={(event) =>
                                                                setContactPassword(
                                                                    event.target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                        <p className="text-xs">
                                                            A new link replaces
                                                            any previous link
                                                            and expires after 24
                                                            hours. Up to three
                                                            requests per day.
                                                        </p>
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <Button
                                                            onClick={
                                                                handleSaveRecovery
                                                            }
                                                            disabled={
                                                                updateSecondaryMutation.isPending ||
                                                                !contactPassword
                                                            }
                                                        >
                                                            {updateSecondaryMutation.isPending ? (
                                                                <>
                                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                                    Saving...
                                                                </>
                                                            ) : (
                                                                "Save"
                                                            )}
                                                        </Button>
                                                        <Button
                                                            variant="outline"
                                                            onClick={() =>
                                                                setShowRecoveryFields(
                                                                    false
                                                                )
                                                            }
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                            </CardContent>
                            <CardFooter className="border-t px-16 py-10 flex flex-col gap-6">
                                <section className="flex justify-center items-center text-center">
                                    <p className="text-sm text-black">
                                        For questions, queries or to request
                                        updates to your profile or application,
                                        contact:
                                        <a
                                            className="ml-2 underline text-app-default"
                                            href="mailto:audrey.souness@andersonstrathern.co.uk"
                                        >
                                            audrey.souness@andersonstrathern.co.uk
                                        </a>
                                    </p>
                                </section>
                            </CardFooter>
                        </Card>
                    </div>
                </div>
                <div></div>
            </main>
        </div>
    )
}
